/**
 * Console log interceptor to send console logs to PostHog as events
 * This works without OpenTelemetry by using PostHog's event capture
 */

// Store original console methods
const originalConsole = {
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error,
  debug: console.debug,
};

/**
 * Get PostHog instance from window (set by PostHog JS SDK)
 */
function getPostHog(): any {
  if (typeof window !== 'undefined') {
    // PostHog JS SDK attaches itself to window.posthog when initialized
    return (window as any).posthog || null;
  }
  return null;
}

/**
 * Initialize console log interception
 * This will capture all console logs and send them to PostHog as events
 */
export function interceptConsoleLogsForPostHog() {
  // Check if already intercepted
  if ((console as any).__posthogIntercepted) {
    return;
  }
  
  (console as any).__posthogIntercepted = true;

  // Intercept console.log
  console.log = (...args: any[]) => {
    originalConsole.log(...args);
    const message = args.map((arg) => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    // Send to PostHog as events (logs endpoint requires OTLP protobuf which is complex)
    // PostHog's built-in console log recording will also capture these
    const posthog = getPostHog();
    if (posthog) {
      try {
        posthog.capture('console_log', {
          level: 'info',
          message: message,
          console_method: 'log',
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        // Silently fail
      }
    }
  };

  // Intercept console.info
  console.info = (...args: any[]) => {
    originalConsole.info(...args);
    const message = args.map((arg) => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    const posthog = getPostHog();
    if (posthog) {
      try {
        posthog.capture('console_log', { level: 'info', message, console_method: 'info' });
      } catch (error) {}
    }
  };

  // Intercept console.warn
  console.warn = (...args: any[]) => {
    originalConsole.warn(...args);
    const message = args.map((arg) => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    const posthog = getPostHog();
    if (posthog) {
      try {
        posthog.capture('console_log', { level: 'warn', message, console_method: 'warn' });
      } catch (error) {}
    }
  };

  // Intercept console.error
  console.error = (...args: any[]) => {
    originalConsole.error(...args);
    const message = args.map((arg) => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    const posthog = getPostHog();
    if (posthog) {
      try {
        posthog.capture('console_log', { level: 'error', message, console_method: 'error' });
      } catch (error) {}
    }
  };

  // Intercept console.debug
  console.debug = (...args: any[]) => {
    originalConsole.debug(...args);
    const message = args.map((arg) => 
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    const posthog = getPostHog();
    if (posthog) {
      try {
        posthog.capture('console_log', { level: 'debug', message, console_method: 'debug' });
      } catch (error) {}
    }
  };
}

/**
 * Restore original console methods (for testing or cleanup)
 */
export function restoreConsoleLogs() {
  console.log = originalConsole.log;
  console.info = originalConsole.info;
  console.warn = originalConsole.warn;
  console.error = originalConsole.error;
  console.debug = originalConsole.debug;
}

